import { AdminShell } from '@/features/admin/components/admin-shell.js';
import { TemplateCreateForm } from '@/features/admin/components/templates/template-create-form.js';
import { TemplateDeprecateDialog } from '@/features/admin/components/templates/template-deprecate-dialog.js';
import { TemplateDetail } from '@/features/admin/components/templates/template-detail.js';
import { TemplatesList } from '@/features/admin/components/templates/templates-list.js';
import { useAuth } from '@/lib/auth/use-auth.js';
import { tokens } from '@/lib/tokens.js';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_admin/templates')({
  component: TemplatesPage,
});

type View = 'list' | 'create' | 'detail';

function ForbiddenMessage() {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: tokens.colors.textMutedDark }}>
      <p style={{ color: tokens.colors.error, fontWeight: 600 }}>
        Super admin access required to manage global templates.
      </p>
    </div>
  );
}

function TemplatesPage() {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showDeprecate, setShowDeprecate] = useState(false);
  const [deprecateCode, setDeprecateCode] = useState('');

  if (!user?.isSuperAdmin) {
    return (
      <AdminShell currentPath="/templates">
        <ForbiddenMessage />
      </AdminShell>
    );
  }

  return (
    <AdminShell currentPath="/templates">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}
        >
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: tokens.colors.textDark,
              margin: 0,
            }}
          >
            Templates
          </h1>
          {view !== 'list' && (
            <button
              type="button"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: tokens.colors.primary,
                cursor: 'pointer',
                fontSize: '0.875rem',
                padding: 0,
              }}
              onClick={() => {
                setView('list');
                setSelectedId(null);
              }}
              aria-label="Back to list"
            >
              ← Back
            </button>
          )}
        </div>

        {view === 'list' && (
          <TemplatesList
            onSelect={(id) => {
              setSelectedId(id);
              setView('detail');
            }}
            onCreateClick={() => setView('create')}
          />
        )}

        {view === 'create' && (
          <TemplateCreateForm onSuccess={() => setView('list')} onCancel={() => setView('list')} />
        )}

        {view === 'detail' && selectedId !== null && (
          <TemplateDetail
            templateId={selectedId}
            onDeprecateClick={() => {
              setDeprecateCode(selectedId);
              setShowDeprecate(true);
            }}
          />
        )}
      </div>

      {showDeprecate && selectedId !== null && (
        <TemplateDeprecateDialog
          templateId={selectedId}
          templateCode={deprecateCode}
          onClose={() => setShowDeprecate(false)}
          onSuccess={() => setView('list')}
        />
      )}
    </AdminShell>
  );
}
